import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, roundTo, toText } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  signedConstant,
  template,
  trap,
} from './authoring.ts';

/**
 * The Grade 11 topics the year was missing.
 *
 * Two authoring pressures shape most of this file.
 *
 * The first is that several of these errors are *not* arithmetic. Whether a
 * radical curve starts at h or at −h, whether tangent's gaps sit where sine or
 * where cosine vanishes, whether a strong correlation is the one nearest +1 or
 * the one largest in size — a student with any of these backwards produces a
 * number that is perfectly correct for the opposite question, and an answer box
 * cannot tell the two apart. Those are multiple choice by necessity, not for
 * convenience.
 *
 * The second is money. A compound-interest answer is stated to the penny, so
 * every tolerance here is written with `decimalTolerance`: the flat figure an
 * author reaches for is *relative*, and on a balance of £3175 a `tolerance` of
 * 0.01 is a band thirty pounds wide — wide enough to accept the simple-interest
 * answer the traps exist to diagnose.
 */

/** A number for prose rather than for LaTeX, where a hyphen is not a minus. */
const minus = (value: number | string): string => String(value).replace(/-/g, '\u2212');

/** To the penny. */
const money = (value: number): number => Math.round(value * 100) / 100;

/** How often interest is added, in words and as a count. */
const PERIODS: { name: string; n: number }[] = [
  { name: 'half-yearly', n: 2 },
  { name: 'quarterly', n: 4 },
  { name: 'monthly', n: 12 },
];

/**
 * Exact tangents of the angles students learn, with the two wrong answers a
 * student actually produces at each: the reciprocal (sine and cosine read off
 * the wrong axes) and the product (divide misread as multiply).
 *
 * The 45° family is absent for the same reason it is absent from the unit
 * circle templates — there sine and cosine are equal, so the reciprocal *is*
 * the correct answer and an option would carry two diagnoses at once.
 */
const TANGENTS: Record<number, { tan: string; cot: string; product: string }> = {
  30: { tan: '\\frac{1}{\\sqrt{3}}', cot: '\\sqrt{3}', product: '\\frac{\\sqrt{3}}{4}' },
  60: { tan: '\\sqrt{3}', cot: '\\frac{1}{\\sqrt{3}}', product: '\\frac{\\sqrt{3}}{4}' },
  120: { tan: '-\\sqrt{3}', cot: '-\\frac{1}{\\sqrt{3}}', product: '-\\frac{\\sqrt{3}}{4}' },
  150: { tan: '-\\frac{1}{\\sqrt{3}}', cot: '-\\sqrt{3}', product: '-\\frac{\\sqrt{3}}{4}' },
  210: { tan: '\\frac{1}{\\sqrt{3}}', cot: '\\sqrt{3}', product: '\\frac{\\sqrt{3}}{4}' },
  240: { tan: '\\sqrt{3}', cot: '\\frac{1}{\\sqrt{3}}', product: '\\frac{\\sqrt{3}}{4}' },
  300: { tan: '-\\sqrt{3}', cot: '-\\frac{1}{\\sqrt{3}}', product: '-\\frac{\\sqrt{3}}{4}' },
  330: { tan: '-\\frac{1}{\\sqrt{3}}', cot: '-\\sqrt{3}', product: '-\\frac{\\sqrt{3}}{4}' },
};

/** The same value with its sign flipped, for the distractor that lost a quadrant. */
const flipSign = (latex: string): string => (latex.startsWith('-') ? latex.slice(1) : `-${latex}`);

/** Pythagorean triples, so a unit-circle point has exact coordinates. */
const TRIPLES: [number, number, number][] = [
  [3, 4, 5],
  [4, 3, 5],
  [5, 12, 13],
  [12, 5, 13],
  [8, 15, 17],
  [15, 8, 17],
  [7, 24, 25],
  [24, 7, 25],
  [20, 21, 29],
  [9, 40, 41],
];

export const GRADE_ELEVEN_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------ radical functions */
  template({
    id: 'radical-domain-start',
    conceptId: 'radical-functions',
    description: 'Find the smallest input a square-root function accepts.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const start = rng.int(-9, 9) || 4;
      // At difficulty 3 a coefficient sits inside, so the start is no longer
      // the number in the bracket and the reading has to be earned.
      const scale = difficulty === 3 ? rng.int(2, 5) : 1;
      const inside = scale === 1 ? `x ${signedConstant(-start)}` : `${scale}x ${signedConstant(-scale * start)}`;

      return numericExercise({
        prompt: 'What is the smallest value of x that this function accepts?',
        latex: `y = \\sqrt{${inside}}`,
        value: start,
        summary: `A square root needs its inside to be zero or more, so solve ${minus(inside.replace(/\s+/g, ' '))} ≥ 0. That gives x ≥ ${minus(start)}, and the curve starts there.`,
        hint: 'Set what is under the root to be greater than or equal to zero, and solve that inequality.',
        steps: [
          { label: 'The root demands a non-negative inside.', latex: `${inside} \\ge 0` },
          { label: 'Solve for x.', latex: `x \\ge ${start}` },
        ],
        // The sign in the bracket read as the direction of the shift.
        traps: [trap(-start, 'radical-start-point-sign-flipped')],
      });
    },
  }),

  template({
    id: 'radical-start-point',
    conceptId: 'radical-functions',
    description: 'Locate the point where a transformed square-root curve begins.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // |h| ≠ |k| and both non-zero, or two of the four options coincide and
      // the item would carry the same diagnosis twice.
      const h = rng.pick([-8, -6, -5, -3, -2, 2, 3, 5, 6, 8]);
      const k = rng.pick([-7, -4, -1, 1, 4, 7]);

      const { choices, correctId } = options(
        [
          { label: `(${minus(h)}, ${minus(k)})`, correct: true },
          { label: `(${minus(-h)}, ${minus(k)})`, misconceptionId: 'radical-start-point-sign-flipped' },
          { label: `(${minus(h)}, ${minus(-k)})`, misconceptionId: 'shift-direction-inverted' },
          { label: `(${minus(k)}, ${minus(h)})`, misconceptionId: 'inside-outside-swapped' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Where does this curve start?',
        latex: `y = \\sqrt{x ${signedConstant(-h)}} ${signedConstant(k)}`,
        choices,
        correctId,
        summary: `The root is zero when x = ${minus(h)}, and there the whole expression is ${minus(k)}. So the curve begins at (${minus(h)}, ${minus(k)}) and travels right from there.`,
        hint: 'Find the input that makes the root exactly zero, then work out y there.',
      });
    },
  }),

  template({
    id: 'radical-domain-choose',
    conceptId: 'radical-functions',
    description: 'Choose the domain of a transformed square-root function.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const h = rng.pick([-9, -7, -6, -4, -2, 2, 4, 6, 7, 9]);
      const k = rng.pick([-5, -3, 3, 5]);

      const { choices, correctId } = options(
        [
          { latex: `x \\ge ${h}`, correct: true },
          { latex: `x \\ge ${-h}`, misconceptionId: 'radical-start-point-sign-flipped' },
          { latex: `y \\ge ${k}`, misconceptionId: 'domain-and-range-swapped' },
          { label: 'every real number', misconceptionId: 'radical-domain-taken-as-all-reals' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What is the domain of this function?',
        latex: `y = \\sqrt{x ${signedConstant(-h)}} ${signedConstant(k)}`,
        choices,
        correctId,
        summary: `The root needs x ${minus(signedConstant(-h))} ≥ 0, so x ≥ ${minus(h)}. The ${minus(k)} outside moves the curve up or down and cannot change which inputs are allowed — y ≥ ${minus(k)} is the range, a different question.`,
        hint: 'The domain is about inputs. Only what sits under the root can forbid one.',
      });
    },
  }),

  template({
    id: 'root-index-domain',
    conceptId: 'radical-functions',
    description: 'Tell an odd root from an even one by what it will accept.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const shifts = rng.shuffle([2, 3, 5, 6, 7, 8]).slice(0, 4) as number[];
      const cubeWanted = rng.bool();
      const [first, second, third, fourth] = shifts as [number, number, number, number];

      // One question, asked in both directions: the odd root among even ones,
      // or the even root among odd ones. The wrong answer differs accordingly.
      const entries = cubeWanted
        ? [
            { latex: `y = \\sqrt[3]{x ${signedConstant(-first)}}`, correct: true },
            { latex: `y = \\sqrt{x ${signedConstant(-second)}}`, misconceptionId: 'radical-domain-taken-as-all-reals' },
            { latex: `y = \\sqrt{x ${signedConstant(third)}}`, misconceptionId: 'radical-domain-taken-as-all-reals' },
            { latex: `y = \\sqrt{x ${signedConstant(-fourth)}}`, misconceptionId: 'radical-domain-taken-as-all-reals' },
          ]
        : [
            { latex: `y = \\sqrt{x ${signedConstant(-first)}}`, correct: true },
            { latex: `y = \\sqrt[3]{x ${signedConstant(-second)}}`, misconceptionId: 'cube-root-domain-restricted' },
            { latex: `y = \\sqrt[3]{x ${signedConstant(third)}}`, misconceptionId: 'cube-root-domain-restricted' },
            { latex: `y = \\sqrt[3]{x ${signedConstant(-fourth)}}`, misconceptionId: 'cube-root-domain-restricted' },
          ];

      const { choices, correctId } = options(entries, rng.shuffle);

      return choiceExercise({
        prompt: cubeWanted
          ? 'Which of these functions is defined for every real number?'
          : 'Which of these functions has inputs it must refuse?',
        choices,
        correctId,
        summary: cubeWanted
          ? 'A cube root accepts negatives, because an odd power keeps the sign of its base: (−2)³ = −8, so ∛(−8) = −2. A square root cannot, because nothing squares to a negative.'
          : 'Only the square root can be stopped. An odd root keeps the sign of its base, so a cube root takes every real number there is; an even root refuses whatever makes its inside negative.',
        hint: 'Ask whether anything, positive or negative, raised to that power could give a negative result.',
      });
    },
  }),

  /* ----------------------------------------------------- exponential models */
  template({
    id: 'compound-interest-amount',
    conceptId: 'exponential-models',
    description: 'Apply the compound interest formula with several periods a year.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const principal = rng.pick([800, 1200, 1500, 2000, 2500, 3000, 4000, 5000]);
      const ratePercent = rng.pick([2, 3, 4, 5, 6, 8]);
      // n = 1 is excluded on purpose: with one period a year the "forgot to
      // divide the rate" trap *is* the correct answer, and the item would tell
      // a right student they had erred.
      const { name, n } = rng.pick(PERIODS);
      const years = rng.int(3, difficulty === 4 ? 12 : 7);

      const rate = ratePercent / 100;
      const value = money(principal * (1 + rate / n) ** (n * years));
      const simple = money(principal * (1 + rate * years));
      const unscaled = money(principal * (1 + rate) ** (n * years));

      return numericExercise({
        prompt: `£${principal} is invested at ${ratePercent}% a year, compounded ${name}. What is it worth after ${years} years? Give your answer to two decimal places.`,
        value,
        tolerance: decimalTolerance(value, 2),
        summary: `Each period pays ${ratePercent}% ÷ ${n}, and there are ${n} × ${years} = ${n * years} of them. So the balance is ${principal}(1 + ${rate}/${n})^${n * years} ≈ £${value.toFixed(2)}.`,
        hint: 'Divide the annual rate among the periods, and multiply the years by the number of periods.',
        steps: [
          { label: 'Name the four quantities.', latex: `P = ${principal},\\ r = ${rate},\\ n = ${n},\\ t = ${years}` },
          { label: 'Divide the rate among the periods.', latex: `\\frac{${rate}}{${n}} = ${roundTo(rate / n, 6)}` },
          { label: 'Count the periods.', latex: `${n} \\times ${years} = ${n * years}` },
          { label: 'Apply the formula.', latex: `A = ${principal}\\left(1 + \\frac{${rate}}{${n}}\\right)^{${n * years}} \\approx ${value.toFixed(2)}` },
        ],
        traps: [
          trap(simple.toFixed(2), 'compound-interest-taken-as-simple'),
          trap(unscaled.toFixed(2), 'compound-periods-not-scaled'),
        ],
      });
    },
  }),

  template({
    id: 'compound-period-rate',
    conceptId: 'exponential-models',
    description: 'Find the rate applied at each compounding period.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const { name, n } = rng.pick(PERIODS);
      const each = rng.pick([0.25, 0.5, 1, 1.5, 2, 3]);
      const annual = roundTo(each * n, 2);
      if (annual > 24) return buildPeriodRate(n, name, 0.25);
      return buildPeriodRate(n, name, each);
    },
  }),

  template({
    id: 'half-life-remaining',
    conceptId: 'exponential-models',
    description: 'Find what is left of a sample after several half-lives.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const halvings = rng.int(2, difficulty === 3 ? 5 : 3);
      const remaining = rng.int(2, 12);
      const start = remaining * 2 ** halvings;
      const halfLife = rng.pick([2, 3, 4, 5, 6, 8, 10]);
      const elapsed = halfLife * halvings;
      const substance = rng.pick(['iodine-131', 'a tracer', 'the isotope', 'the sample']);

      return numericExercise({
        prompt: `${start} grams of ${substance} has a half-life of ${halfLife} days. How many grams remain after ${elapsed} days?`,
        unit: 'g',
        value: remaining,
        summary: `${elapsed} ÷ ${halfLife} = ${halvings} half-lives, so the amount is halved ${halvings} times: ${start} ÷ 2^${halvings} = ${remaining} g.`,
        hint: 'Count the halvings first. That count is an exponent, not a divisor.',
        steps: [
          { label: 'Count the half-lives.', latex: `\\frac{${elapsed}}{${halfLife}} = ${halvings}` },
          { label: 'Halve that many times.', latex: `${start} \\times \\left(\\tfrac{1}{2}\\right)^{${halvings}}` },
          { label: 'Evaluate.', latex: `${remaining}` },
        ],
        // Dividing by the count of halvings instead of halving that many times.
        traps: [trap(roundTo(start / halvings, 4), 'half-life-divided-not-halved')],
      });
    },
  }),

  template({
    id: 'growth-model-choose',
    conceptId: 'exponential-models',
    description: 'Choose the model for a quantity growing by a fixed percentage.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const start = rng.pick([600, 800, 1200, 2500, 4000, 5000, 8000, 12000]);
      const percent = rng.pick([2, 3, 4, 5, 6, 7, 8]);
      const decimal = (percent / 100).toFixed(2);
      const factor = (1 + percent / 100).toFixed(2);
      const thing = rng.pick(['A town of', 'A colony of', 'A herd of', 'A population of']);

      const { choices, correctId } = options(
        [
          { latex: `P = ${start}(${factor})^{t}`, correct: true },
          { latex: `P = ${start}(${decimal})^{t}`, misconceptionId: 'growth-rate-used-as-factor' },
          { latex: `P = ${start}(1 + ${decimal}t)`, misconceptionId: 'exponential-treated-as-linear' },
          { latex: `P = ${start}(${1 + percent})^{t}`, misconceptionId: 'percent-not-converted' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${thing} ${start} grows by ${percent}% a year. Which model gives the population after t years?`,
        choices,
        correctId,
        summary: `Growing by ${percent}% leaves ${100 + percent}% of what was there, so the multiplier is ${factor}. Multiplying by ${decimal} would keep only the growth and throw away the population itself.`,
        hint: 'After one year, how many times the starting population is there? That number is the multiplier.',
      });
    },
  }),

  template({
    id: 'depreciation-value',
    conceptId: 'exponential-models',
    description: 'Find a value after several years of percentage decay.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const start = rng.pick([9000, 12000, 15000, 18000, 24000, 30000]);
      const percent = rng.pick([8, 10, 12, 15, 18, 20]);
      const years = rng.int(2, 4);

      const factor = 1 - percent / 100;
      const value = money(start * factor ** years);
      const linear = money(start * (1 - (percent * years) / 100));
      const factorOnly = money(start * (percent / 100) ** years);

      return numericExercise({
        prompt: `A machine worth £${start} loses ${percent}% of its value each year. What is it worth after ${years} years? Give your answer to two decimal places.`,
        value,
        tolerance: decimalTolerance(value, 2),
        summary: `Losing ${percent}% means keeping ${100 - percent}%, so each year multiplies by ${factor}. After ${years} years the value is ${start} × ${factor}^${years} ≈ £${value.toFixed(2)}.`,
        hint: 'Each year the loss is taken from the new value, not from the original.',
        steps: [
          { label: 'Losing means keeping the rest.', latex: `b = ${factor}` },
          { label: 'Write the model.', latex: `V = ${start} \\times ${factor}^{t}` },
          { label: `Substitute t = ${years}.`, latex: `${value.toFixed(2)}` },
        ],
        traps: [
          trap(linear.toFixed(2), 'exponential-treated-as-linear'),
          trap(factorOnly.toFixed(2), 'growth-rate-used-as-factor'),
        ],
      });
    },
  }),

  /* ------------------------------------------ common and natural logarithms */
  template({
    id: 'log-base-evaluate',
    conceptId: 'natural-logarithm',
    description: 'Evaluate a common or a natural logarithm of an exact power.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const power = rng.int(2, 9);
      const natural = rng.bool();

      // The wrong button: the same number read in the other base.
      const other = natural ? roundTo(power * Math.LOG10E, 2) : roundTo(power * Math.LN10, 2);

      return numericExercise({
        prompt: 'Evaluate this logarithm exactly.',
        latex: natural ? `\\ln e^{${power}}` : `\\log 10^{${power}}`,
        value: power,
        summary: natural
          ? `ln is the logarithm to base e, so ln(e^${power}) asks what power of e gives e^${power}. The answer is ${power}.`
          : `A log with no base written means base 10, so log(10^${power}) asks what power of 10 gives 10^${power}. The answer is ${power}.`,
        hint: 'Written without a base, log means base 10. ln means base e. Which one is in front of you?',
        steps: [
          { label: 'Read the definition backwards.', latex: natural ? `\\ln e^{k} = k` : `\\log 10^{k} = k` },
          { label: 'Apply it.', latex: `${power}` },
        ],
        traps: [trap(other, 'natural-log-read-as-base-ten')],
      });
    },
  }),

  template({
    id: 'natural-log-solve',
    conceptId: 'natural-logarithm',
    description: 'Solve an equation with the unknown in an exponent of e.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const coefficient = rng.int(2, 6);
      const target = rng.pick([5, 8, 12, 15, 20, 30, 40, 50, 60, 80, 100, 150]);

      const value = roundTo(Math.log(target) / coefficient, 2);
      const commonLog = roundTo(Math.log10(target) / coefficient, 2);
      const undivided = roundTo(Math.log(target), 2);

      return numericExercise({
        prompt: 'Solve for x, giving your answer to two decimal places.',
        latex: `e^{${coefficient}x} = ${target}`,
        value,
        tolerance: decimalTolerance(value, 2),
        summary: `Take ln of both sides. ln(e^${coefficient}x) is just ${coefficient}x, because ln and e undo each other, so ${coefficient}x = ln ${target} ≈ ${undivided}, giving x ≈ ${value}.`,
        hint: 'ln is the operation that undoes e. Take it on both sides, and the exponent comes straight down.',
        steps: [
          { label: 'Take ln of both sides.', latex: `${coefficient}x = \\ln ${target}` },
          { label: 'Evaluate the logarithm.', latex: `${coefficient}x \\approx ${undivided}` },
          { label: 'Divide by the coefficient.', latex: `x \\approx ${value}` },
        ],
        traps: [
          trap(commonLog, 'natural-log-read-as-base-ten'),
          trap(undivided, 'exponent-divided-out'),
        ],
      });
    },
  }),

  template({
    id: 'continuous-growth-amount',
    conceptId: 'natural-logarithm',
    description: 'Evaluate a continuously compounded amount.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const principal = rng.pick([500, 800, 1000, 1500, 2000, 2500, 4000]);
      const percent = rng.pick([3, 4, 5, 6, 7, 8, 10, 12]);
      const years = rng.int(3, 12);

      const rate = percent / 100;
      const value = money(principal * Math.exp(rate * years));
      const rateOnly = money(principal * Math.exp(rate));
      const eAsThree = money(principal * 3 ** (rate * years));

      return numericExercise({
        prompt: `£${principal} is invested at ${percent}% a year, compounded continuously. What is it worth after ${years} years? Give your answer to two decimal places.`,
        latex: `A = Pe^{rt}`,
        value,
        tolerance: decimalTolerance(value, 2),
        summary: `The exponent is rt = ${rate} × ${years} = ${roundTo(rate * years, 4)}, so A = ${principal}e^${roundTo(rate * years, 4)} ≈ £${value.toFixed(2)}.`,
        hint: 'The rate and the time are multiplied inside the exponent — both of them, every time.',
        steps: [
          { label: 'Multiply the rate by the time.', latex: `rt = ${rate} \\times ${years} = ${roundTo(rate * years, 4)}` },
          { label: 'Apply the formula.', latex: `A = ${principal}e^{${roundTo(rate * years, 4)}}` },
          { label: 'Evaluate.', latex: `\\approx ${value.toFixed(2)}` },
        ],
        traps: [
          trap(rateOnly.toFixed(2), 'continuous-rate-not-multiplied-by-time'),
          trap(eAsThree.toFixed(2), 'natural-base-rounded-away'),
        ],
      });
    },
  }),

  template({
    id: 'continuous-growth-time',
    conceptId: 'natural-logarithm',
    description: 'Find how long a continuous model takes to reach a target.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const start = rng.pick([200, 300, 400, 500, 800, 1000]);
      const multiple = rng.pick([2, 3, 4, 5]);
      const percent = rng.pick([4, 5, 6, 8, 10, 12]);

      const rate = percent / 100;
      const target = start * multiple;
      const value = roundTo(Math.log(multiple) / rate, 2);
      const undivided = roundTo(Math.log(multiple), 2);
      const commonLog = roundTo(Math.log10(multiple) / rate, 2);

      return numericExercise({
        prompt: `A culture of ${start} bacteria grows continuously at ${percent}% an hour. After how many hours does it reach ${target}? Give your answer to two decimal places.`,
        value,
        tolerance: decimalTolerance(value, 2),
        summary: `Divide by the starting amount first: e^(${rate}t) = ${multiple}. Taking ln brings the exponent down, so ${rate}t = ln ${multiple} ≈ ${undivided} and t ≈ ${value} hours.`,
        hint: 'Divide by the starting amount before taking any logarithm — it clears everything that is not the unknown.',
        steps: [
          { label: 'Write the model.', latex: `${start}e^{${rate}t} = ${target}` },
          { label: 'Divide by the starting amount.', latex: `e^{${rate}t} = ${multiple}` },
          { label: 'Take ln of both sides.', latex: `${rate}t = \\ln ${multiple} \\approx ${undivided}` },
          { label: 'Divide by the rate.', latex: `t \\approx ${value}` },
        ],
        traps: [
          trap(undivided, 'continuous-rate-not-multiplied-by-time'),
          trap(commonLog, 'natural-log-read-as-base-ten'),
        ],
      });
    },
  }),

  /* -------------------------------------------------------- tangent function */
  template({
    id: 'tangent-special-angle',
    conceptId: 'tangent-function',
    description: 'Give the exact tangent of one of the special angles.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const angle = Number(rng.pick(Object.keys(TANGENTS)));
      const entry = TANGENTS[angle] as { tan: string; cot: string; product: string };

      const { choices, correctId } = options(
        [
          { latex: entry.tan, correct: true },
          { latex: flipSign(entry.tan), misconceptionId: 'quadrant-sign-ignored' },
          { latex: entry.cot, misconceptionId: 'sine-cosine-coordinates-swapped' },
          { latex: entry.product, misconceptionId: 'tangent-as-product' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `What is the exact value of tan ${angle}°?`,
        choices,
        correctId,
        summary: `tan θ is the y-coordinate divided by the x-coordinate on the unit circle — sine over cosine, never sine times cosine. At ${angle}° that quotient is the option shown.`,
        hint: 'Write down sine and cosine for the angle first, then divide the first by the second.',
      });
    },
  }),

  template({
    id: 'tangent-asymptote',
    conceptId: 'tangent-function',
    description: 'Say where the tangent graph has a vertical asymptote.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const correct = 90 + 180 * rng.int(0, 3);
      const wrongs = rng.shuffle([0, 180, 360, 540, 720]).slice(0, 3) as number[];

      const { choices, correctId } = options(
        [
          { label: `${correct}°`, correct: true },
          ...wrongs.map((angle) => ({
            label: `${angle}°`,
            misconceptionId: 'tangent-asymptote-from-numerator',
          })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'At which of these angles does the graph of y = tan θ have a vertical asymptote?',
        choices,
        correctId,
        summary: `tan θ = sin θ ÷ cos θ, and a quotient blows up where the bottom is zero. cos ${correct}° = 0, so there is an asymptote there. At a multiple of 180° it is sine that vanishes, which makes tangent zero — a crossing, not a gap.`,
        hint: 'Which of sine and cosine is on the bottom? That is the one whose zeros cause trouble.',
      });
    },
  }),

  template({
    id: 'tangent-second-solution',
    conceptId: 'tangent-function',
    description: 'Use a period of 180° to find the second solution in a turn.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // 90° is excluded: tangent is undefined there, so it is not a solution
      // of anything, and 180 − 90 would collide with the answer.
      const first = rng.pick([12, 18, 23, 27, 34, 41, 48, 56, 63, 71, 78, 104, 112, 127, 138, 149, 156, 163]);
      const answer = first + 180;

      return numericExercise({
        prompt: `tan θ has a solution at θ = ${first}°. What is the other solution with 0° ≤ θ < 360°?`,
        unit: '°',
        value: answer,
        summary: `Tangent repeats every 180°, not every 360°, because half a turn changes the sign of both coordinates and a quotient of two negatives is unchanged. So the other solution is ${first} + 180 = ${answer}°.`,
        hint: 'How far along does the tangent graph go before it repeats? It is not a full turn.',
        steps: [
          { label: 'The period of tangent is 180°.', latex: '\\tan(\\theta + 180^{\\circ}) = \\tan\\theta' },
          { label: 'Add it.', latex: `${first} + 180 = ${answer}` },
        ],
        traps: [
          trap(first + 360, 'tangent-period-taken-as-two-pi'),
          trap(180 - first, 'general-solution-period-wrong'),
        ],
      });
    },
  }),

  template({
    id: 'tangent-from-coordinates',
    conceptId: 'tangent-function',
    description: 'Compute a tangent from the coordinates of a unit-circle point.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const [a, b, c] = rng.pick(TRIPLES);
      const sinNegative = rng.bool();
      const cosNegative = rng.bool();
      const sinN = sinNegative ? -a : a;
      const cosN = cosNegative ? -b : b;

      const answer = fraction(sinN, cosN);
      const swapped = fraction(cosN, sinN);
      const product = fraction(sinN * cosN, c * c);

      return fractionExercise({
        prompt: 'A point on the unit circle has these coordinates. What is tan θ?',
        latex: `\\cos\\theta = \\frac{${cosN}}{${c}}, \\quad \\sin\\theta = \\frac{${sinN}}{${c}}`,
        value: answer,
        requireSimplified: true,
        summary: `tan θ = sin θ ÷ cos θ. The ${c}s cancel, leaving ${sinN} ÷ ${cosN} = ${toText(answer)}.`,
        hint: 'Divide the y-coordinate by the x-coordinate. The denominators are the same, so they cancel.',
        steps: [
          { label: 'Tangent is the quotient.', latex: `\\tan\\theta = \\frac{${sinN}/${c}}{${cosN}/${c}}` },
          { label: 'The denominators cancel.', latex: `\\frac{${sinN}}{${cosN}}` },
          { label: 'Simplify.', latex: toText(answer) },
        ],
        traps: [
          fractionTrap(swapped, 'sine-cosine-coordinates-swapped'),
          fractionTrap(product, 'tangent-as-product'),
        ],
      });
    },
  }),

  /* ----------------------------------------------------- recursive sequences */
  template({
    id: 'recursive-term-from-rule',
    conceptId: 'recursive-sequences',
    description: 'Apply a recursive rule several times to reach a later term.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const first = rng.int(2, 12);
      const step = rng.pick([-9, -7, -5, -4, -3, 3, 4, 5, 6, 7, 8]);
      const wanted = rng.int(difficulty === 3 ? 5 : 4, difficulty === 3 ? 8 : 6);

      const terms = [first];
      for (let n = 1; n < wanted; n += 1) terms.push((terms[n - 1] as number) + step);
      const answer = terms[wanted - 1] as number;

      return numericExercise({
        prompt: `A sequence has a₁ = ${first} and each term is the one before it plus ${minus(step)}. What is a${subscript(wanted)}?`,
        latex: `a_{1} = ${first}, \\quad a_{n} = a_{n-1} ${signedConstant(step)}`,
        value: answer,
        summary: `Write the terms out: ${terms.map((t) => minus(t)).join(', ')}. Reaching a${subscript(wanted)} takes ${wanted - 1} applications of the rule, not one and not ${wanted}.`,
        hint: 'The rule takes the previous term, so you cannot skip. Write the terms in a row and count.',
        steps: [
          { label: 'Start from the first term.', latex: `a_{1} = ${first}` },
          { label: 'Apply the rule each time.', latex: terms.map((t) => String(t)).join(',\\ ') },
          { label: 'Read off the one asked for.', latex: `a_{${wanted}} = ${answer}` },
        ],
        traps: [
          // Substituting the term number into a rule that wants a term.
          trap(first + step * wanted, 'recursive-rule-used-as-explicit'),
          // One step taken however far the target was.
          trap(first + step, 'recursion-applied-once'),
        ],
      });
    },
  }),

  template({
    id: 'recursive-to-explicit',
    conceptId: 'recursive-sequences',
    description: 'Convert a recursive definition into an explicit formula.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const first = rng.int(2, 15);
      const step = rng.pick([-8, -6, -5, -3, 3, 4, 5, 6, 7, 9]);

      const { choices, correctId } = options(
        [
          { latex: `a_{n} = ${first} ${signedConstant(step)}(n - 1)`, correct: true },
          { latex: `a_{n} = ${first} ${signedConstant(step)}n`, misconceptionId: 'recursive-rule-used-as-explicit' },
          { latex: `a_{n} = ${step}n`, misconceptionId: 'recursive-seed-ignored' },
          { latex: `a_{n} = ${first} \\cdot ${step}^{\\,n-1}`, misconceptionId: 'sequence-rule-confused' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which explicit formula describes this sequence?',
        latex: `a_{1} = ${first}, \\quad a_{n} = a_{n-1} ${signedConstant(step)}`,
        choices,
        correctId,
        summary: `The rule adds ${minus(step)}, so the sequence is arithmetic with d = ${minus(step)}. Reaching the nth term takes n − 1 additions from the first, which is what the (n − 1) records — check it at n = 1 and you get ${minus(first)} back.`,
        hint: 'How many times has the rule been applied by the time you reach the nth term?',
      });
    },
  }),

  template({
    id: 'recursive-from-terms',
    conceptId: 'recursive-sequences',
    description: 'Choose the recursive definition matching the first few terms.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const first = rng.int(2, 9);
      const geometric = rng.bool();
      const step = geometric ? rng.int(2, 4) : rng.pick([3, 4, 5, 6, 7]);

      const terms = [first];
      for (let n = 1; n < 4; n += 1) {
        terms.push(geometric ? (terms[n - 1] as number) * step : (terms[n - 1] as number) + step);
      }

      const correct = geometric
        ? `a_{1} = ${first}, \\ a_{n} = ${step}a_{n-1}`
        : `a_{1} = ${first}, \\ a_{n} = a_{n-1} + ${step}`;
      const swapped = geometric
        ? `a_{1} = ${first}, \\ a_{n} = a_{n-1} + ${step}`
        : `a_{1} = ${first}, \\ a_{n} = ${step}a_{n-1}`;
      const seedless = geometric ? `a_{n} = ${step}a_{n-1}` : `a_{n} = a_{n-1} + ${step}`;
      const wrongSeed = geometric
        ? `a_{1} = ${step}, \\ a_{n} = ${first}a_{n-1}`
        : `a_{1} = ${step}, \\ a_{n} = a_{n-1} + ${first}`;

      const { choices, correctId } = options(
        [
          { latex: correct, correct: true },
          { latex: swapped, misconceptionId: 'sequence-rule-confused' },
          { latex: seedless, misconceptionId: 'recursive-seed-ignored' },
          { latex: wrongSeed, misconceptionId: 'recursive-seed-ignored' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which recursive definition gives the sequence ${terms.join(', ')}, …?`,
        choices,
        correctId,
        summary: geometric
          ? `Each term is ${step} times the one before, and the sequence starts at ${first}. Both statements are needed: without the first term the same rule describes infinitely many sequences.`
          : `Each term is ${step} more than the one before, and the sequence starts at ${first}. Both statements are needed: without the first term the same rule describes infinitely many sequences.`,
        hint: 'Test what takes you from one term to the next — and check the definition says where to start.',
      });
    },
  }),

  template({
    id: 'two-step-recursion',
    conceptId: 'recursive-sequences',
    description: 'Extend a sequence whose rule looks back two terms.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const first = rng.int(1, 6);
      const second = rng.int(2, 9);
      const wanted = difficulty === 4 ? rng.int(7, 8) : rng.int(5, 6);

      const terms = [first, second];
      for (let n = 2; n < wanted; n += 1) {
        terms.push((terms[n - 1] as number) + (terms[n - 2] as number));
      }
      const answer = terms[wanted - 1] as number;

      return numericExercise({
        prompt: `A sequence begins a₁ = ${first} and a₂ = ${second}, and every term after that is the sum of the two before it. What is a${subscript(wanted)}?`,
        latex: `a_{n} = a_{n-1} + a_{n-2}`,
        value: answer,
        summary: `Building it out: ${terms.map((t) => String(t)).join(', ')}. A rule that looks back two terms needs two starting values, which is why this sequence is given both.`,
        hint: 'Add the last two terms you have to get the next one, and keep going until you reach the one asked for.',
        steps: [
          { label: 'Start from the two given terms.', latex: `${first},\\ ${second}` },
          { label: 'Add the last two each time.', latex: terms.map((t) => String(t)).join(',\\ ') },
          { label: 'Read off the one asked for.', latex: `a_{${wanted}} = ${answer}` },
        ],
        // The last term reached instead of the one asked for.
        traps: [trap(terms[wanted - 2] as number, 'recursion-applied-once')],
      });
    },
  }),

  /* ----------------------------------------------------- normal distributions */
  template({
    id: 'z-score-compute',
    conceptId: 'normal-distribution',
    description: 'Convert a value into a z-score.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const mean = rng.pick([50, 60, 70, 80, 100, 120, 150, 200, 500]);
      const sd = rng.pick([2, 4, 5, 8, 10, 12, 15, 20]);
      const steps = rng.pick(difficulty === 3 ? [-2.5, -1.5, -0.5, 0.5, 1.5, 2.5] : [-3, -2, -1, 1, 2, 3]);
      const value = roundTo(mean + steps * sd, 4);

      return numericExercise({
        prompt: `A measurement is normally distributed with mean ${mean} and standard deviation ${sd}. What is the z-score of ${minus(value)}?`,
        value: steps,
        summary: `Subtract the mean to get the distance: ${minus(value)} − ${mean} = ${minus(roundTo(value - mean, 4))}. Then divide by the standard deviation to say how many standard deviations that is: ${minus(steps)}.`,
        hint: 'A z-score counts standard deviations, so the division is not optional.',
        steps: [
          { label: 'Find the distance from the mean.', latex: `${value} - ${mean} = ${roundTo(value - mean, 4)}` },
          { label: 'Divide by the standard deviation.', latex: `\\frac{${roundTo(value - mean, 4)}}{${sd}} = ${steps}` },
        ],
        // The distance in the original units, never converted.
        traps: [trap(roundTo(value - mean, 4), 'z-score-not-divided-by-sd')],
      });
    },
  }),

  template({
    id: 'empirical-rule-tail',
    conceptId: 'normal-distribution',
    description: 'Find the proportion beyond a whole number of standard deviations.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const band = rng.pick([
        { k: 1, inside: 68, tail: 16 },
        { k: 2, inside: 95, tail: 2.5 },
        { k: 3, inside: 99.7, tail: 0.15 },
      ]);
      const mean = rng.pick([100, 120, 200, 250, 400, 500]);
      const sd = rng.pick([5, 10, 15, 20, 25]);
      const cut = mean + band.k * sd;
      const outside = roundTo(100 - band.inside, 2);

      return numericExercise({
        prompt: `A quantity is normally distributed with mean ${mean} and standard deviation ${sd}. What percentage of values are above ${cut}?`,
        unit: '%',
        value: band.tail,
        summary: `${cut} is ${band.k} standard deviation${band.k === 1 ? '' : 's'} above the mean, and ${band.inside}% of values lie within that. The remaining ${outside}% is split equally between the two tails, so the upper one holds ${band.tail}%.`,
        hint: 'Find what is left outside the band first, then remember it is shared between two tails.',
        steps: [
          { label: 'How many standard deviations up is it?', latex: `\\frac{${cut} - ${mean}}{${sd}} = ${band.k}` },
          { label: 'The band holds this much.', latex: `${band.inside}\\%` },
          { label: 'What is left over.', latex: `100 - ${band.inside} = ${outside}\\%` },
          { label: 'Halve it for one tail.', latex: `\\frac{${outside}}{2} = ${band.tail}\\%` },
        ],
        traps: [
          trap(outside, 'empirical-rule-tail-doubled'),
          trap(roundTo(100 - band.tail, 2), 'normal-area-complemented'),
        ],
      });
    },
  }),

  template({
    id: 'empirical-rule-count',
    conceptId: 'normal-distribution',
    description: 'Count how many of a group fall within a band of standard deviations.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const band = rng.pick([
        { k: 1, inside: 68 },
        { k: 2, inside: 95 },
      ]);
      const size = rng.pick([200, 400, 500, 800, 1000, 2000]);
      const mean = rng.pick([60, 75, 100, 150, 250]);
      const sd = rng.pick([4, 5, 10, 12, 20]);
      const answer = roundTo((size * band.inside) / 100, 2);
      const outside = roundTo(size - answer, 2);

      return numericExercise({
        prompt: `${size} measurements are normally distributed with mean ${mean} and standard deviation ${sd}. About how many lie between ${mean - band.k * sd} and ${mean + band.k * sd}?`,
        value: answer,
        summary: `Those two values are ${band.k} standard deviation${band.k === 1 ? '' : 's'} either side of the mean, and about ${band.inside}% of a normal distribution lies there. ${band.inside}% of ${size} is about ${answer}.`,
        hint: 'Work out how many standard deviations the endpoints are from the mean, then use the rule.',
        steps: [
          { label: 'How far out are the endpoints?', latex: `${band.k} \\text{ standard deviations}` },
          { label: 'The rule gives the proportion.', latex: `${band.inside}\\%` },
          { label: 'Take that percentage of the group.', latex: `${band.inside}\\% \\times ${size} = ${answer}` },
        ],
        // The count outside the band rather than inside it.
        traps: [trap(outside, 'normal-area-complemented')],
      });
    },
  }),

  template({
    id: 'normal-shape-choose',
    conceptId: 'normal-distribution',
    description: 'Judge whether the empirical rule applies to a distribution.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const bell = rng.pick([
        'the heights of adult women in a city',
        'the weights of loaves from one bakery',
        'the errors in a thousand careful measurements of the same length',
        'the diameters of ball bearings from one machine',
      ]);
      const skewed = rng.shuffle([
        'household incomes in a country',
        'the time customers wait in a phone queue',
        'the number of children per household',
        'marks on a test almost everyone scored full marks on',
        'the ages of people in a primary school',
      ]).slice(0, 3) as string[];

      const { choices, correctId } = options(
        [
          { label: bell, correct: true },
          ...skewed.map((label) => ({ label, misconceptionId: 'normal-assumed-for-any-data' })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'The 68–95–99.7 rule is being applied. For which of these is that justified?',
        choices,
        correctId,
        summary: `The rule describes a *normal* distribution — symmetric, single-peaked, thin at both ends. ${bell.charAt(0).toUpperCase()}${bell.slice(1)} fits that. Incomes have a long right tail, waiting times pile up near zero, and marks against a ceiling bunch at one end, and none of those percentages apply to any of them.`,
        hint: 'Ask whether a histogram of it would be symmetric with a single hump, or lopsided.',
      });
    },
  }),

  /* ------------------------------------------------ regression and correlation */
  template({
    id: 'regression-predict',
    conceptId: 'regression',
    description: 'Predict from a line of best fit, and read its intercept correctly.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const intercept = rng.int(10, 60);
      const slope = rng.pick([1.5, 2, 2.5, 3, 4, 5, 6.5]);
      const x = rng.int(2, 9);
      const value = roundTo(intercept + slope * x, 2);

      return numericExercise({
        prompt: `A line of best fit is ŷ = ${intercept} + ${slope}x, fitted for x between 1 and 10. What does it predict when x = ${x}? Give your answer to two decimal places.`,
        value,
        tolerance: decimalTolerance(value, 2),
        summary: `Substitute: ${intercept} + ${slope} × ${x} = ${value}. The ${intercept} is the prediction at x = 0 and the ${slope} is the change per unit of x — they do different jobs and are not interchangeable.`,
        hint: 'Put the value of x into the equation. Both numbers are used, and each in its own place.',
        steps: [
          { label: 'Substitute x.', latex: `\\hat{y} = ${intercept} + ${slope}(${x})` },
          { label: 'Evaluate.', latex: `${value}` },
        ],
        // Slope and intercept exchanged.
        traps: [trap(roundTo(slope + intercept * x, 2), 'intercept-read-as-slope')],
      });
    },
  }),

  template({
    id: 'regression-residual',
    conceptId: 'regression',
    description: 'Compute a residual, keeping its sign.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const intercept = rng.int(5, 40);
      const slope = rng.pick([2, 3, 4, 5, 6]);
      const x = rng.int(2, 9);
      const predicted = intercept + slope * x;
      const offset = rng.pick([-9, -7, -5, -4, -3, 3, 4, 6, 8, 11]);
      const actual = predicted + offset;

      return numericExercise({
        prompt: `A line of best fit is ŷ = ${intercept} + ${slope}x. At x = ${x} the observed value was ${actual}. What is the residual?`,
        value: offset,
        summary: `The line predicts ${intercept} + ${slope} × ${x} = ${predicted}, and the point was actually at ${actual}. A residual is actual − predicted, so it is ${minus(actual)} − ${predicted} = ${minus(offset)} — ${offset > 0 ? 'positive, because the point sits above the line and the line guessed too low' : 'negative, because the point sits below the line and the line guessed too high'}.`,
        hint: 'Residual is actual minus predicted, in that order. The sign says which side of the line the point is on.',
        steps: [
          { label: 'Predict at that x.', latex: `\\hat{y} = ${intercept} + ${slope}(${x}) = ${predicted}` },
          { label: 'Subtract the prediction from the observation.', latex: `${actual} - ${predicted} = ${offset}` },
        ],
        traps: [trap(-offset, 'regression-residual-sign-inverted')],
      });
    },
  }),

  template({
    id: 'correlation-strongest',
    conceptId: 'regression',
    description: 'Rank correlation coefficients by strength rather than by value.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const strongest = rng.pick([-0.94, -0.91, -0.88, -0.86, -0.82]);
      const others = rng.shuffle([0.71, 0.64, 0.55, 0.43, 0.31, 0.18, -0.24, -0.37]).slice(0, 3) as number[];

      const { choices, correctId } = options(
        [
          { label: `r = ${minus(strongest)}`, correct: true },
          ...others.map((value) => ({
            label: `r = ${minus(value)}`,
            misconceptionId: 'correlation-sign-dropped',
          })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Four studies report these correlation coefficients. Which describes the strongest linear relationship?',
        choices,
        correctId,
        summary: `Strength is the *size* of r and the sign is only the direction. ${minus(strongest)} is ${Math.abs(strongest)} away from zero — further than any of the others — so it is the strongest relationship, with y falling as x rises.`,
        hint: 'Ignore the signs for a moment and ask which is furthest from zero.',
      });
    },
  }),

  template({
    id: 'regression-slope-meaning',
    conceptId: 'regression',
    description: 'Interpret the slope of a line of best fit in context.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const slope = rng.pick([2.4, 3.1, 4.5, 5.2, 6.5, 7.8]);
      const intercept = rng.int(20, 55);
      const r = rng.pick([0.78, 0.81, 0.86, 0.9]);

      const { choices, correctId } = options(
        [
          {
            label: `Each extra hour of study is associated with about ${slope} more marks`,
            correct: true,
          },
          {
            label: `Each extra hour of study causes about ${slope} more marks`,
            misconceptionId: 'correlation-taken-as-cause',
          },
          {
            label: `The correlation between study time and score is ${slope}`,
            misconceptionId: 'correlation-read-from-slope',
          },
          {
            label: `A student who does not study at all scores about ${slope} marks`,
            misconceptionId: 'intercept-read-as-slope',
          },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Study time in hours against test score gives ŷ = ${intercept} + ${slope}x, with r = ${r}. What does the ${slope} mean?`,
        choices,
        correctId,
        summary: `The slope is the predicted change in score for one extra hour, and "associated with" is as strong a claim as a regression can make — students who study more may also attend more and sleep better. The value at x = 0 is the intercept, ${intercept}, and the correlation is r = ${r}; those are two other numbers answering two other questions.`,
        hint: 'The slope is a rate: it says how much y changes per unit of x, and nothing about why.',
      });
    },
  }),

  template({
    id: 'regression-extrapolation',
    conceptId: 'regression',
    description: 'Say which prediction the fitted range actually supports.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const low = rng.int(1, 4);
      const high = low + rng.int(5, 9);
      const inside = rng.int(low + 1, high - 1);
      const outside = rng.shuffle([high + 12, high + 20, high + 35, high + 50]).slice(0, 3) as number[];

      const { choices, correctId } = options(
        [
          { label: `x = ${inside}`, correct: true },
          ...outside.map((value) => ({
            label: `x = ${value}`,
            misconceptionId: 'extrapolated-beyond-the-data',
          })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A line of best fit was calculated from data with x running from ${low} to ${high}. For which of these values of x is a prediction supported by the data?`,
        choices,
        correctId,
        summary: `Only x = ${inside} sits inside the range that was actually measured. Outside ${low} to ${high} nothing was observed, so the line has no evidence about what happens there — it will still produce a number, and that number is an assumption rather than a prediction.`,
        hint: 'The line knows only about the range it was fitted to.',
      });
    },
  }),
];

/** Subscripts for term numbers, so a₆ reads as a₆ in a prompt rather than a6. */
function subscript(value: number): string {
  const DIGITS = '₀₁₂₃₄₅₆₇₈₉';
  return String(value)
    .split('')
    .map((digit) => DIGITS[Number(digit)] as string)
    .join('');
}

/**
 * The per-period rate item, extracted so the guard against an absurd annual
 * rate has somewhere to fall back to rather than a second copy of the body.
 */
function buildPeriodRate(n: number, name: string, each: number): ReturnType<typeof numericExercise> {
  const annual = roundTo(each * n, 2);
  return numericExercise({
    prompt: `An account pays ${annual}% a year, compounded ${name}. What percentage is added to the balance at each of those ${n} times?`,
    unit: '%',
    value: each,
    summary: `The annual rate is shared among the periods: ${annual} ÷ ${n} = ${each}% each time. Applying the full ${annual}% every period would pay the year's interest ${n} times over.`,
    hint: 'Compounding more often does not pay more each time. The annual rate is divided among the periods.',
    steps: [
      { label: 'Divide the annual rate by the number of periods.', latex: `\\frac{${annual}}{${n}} = ${each}` },
    ],
    traps: [trap(annual, 'compound-periods-not-scaled')],
  });
}
